import { keccak256, encodeAbiParameters, toBytes, isAddress, getAddress } from 'viem';

export const ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CLAIM_TYPES = ['refund', 'return', 'exchange', 'cancellation', 'complaint'];
const M = 30;
const BASE6 = 30n ** 6n;

const strHash = (s) => keccak256(toBytes(s));

export function computeClaimId({ principal, merchant, orderRef, claimType, claimNumber }) {
  if (!isAddress(principal)) throw new Error('principal must be a 0x address');
  if (!CLAIM_TYPES.includes(claimType)) throw new Error(`claimType must be one of: ${CLAIM_TYPES.join(', ')}`);
  const n = Number(claimNumber);
  if (!Number.isInteger(n) || n < 1 || n > 65535) throw new Error('claimNumber must be a whole number from 1 to 65535');
  return keccak256(encodeAbiParameters(
    [{ type: 'address' }, { type: 'bytes32' }, { type: 'bytes32' }, { type: 'bytes32' }, { type: 'uint16' }],
    [getAddress(principal), strHash(merchant), strHash(orderRef), strHash(claimType), n],
  ));
}

function step(p, v) {
  let q = (p + v) % M;
  if (q === 0) q = M;
  return (q * 2) % (M + 1);
}

export function checkChar(values) {
  const p = values.reduce(step, M);
  return ALPHABET[(M + 1 - p) % M];
}

export function spokenCode(claimId) {
  let n = BigInt(claimId.slice(0, 18)) % BASE6;
  const vals = [];
  for (let i = 0; i < 6; i += 1) { vals.unshift(Number(n % 30n)); n /= 30n; }
  return vals.map((v) => ALPHABET[v]).join('') + checkChar(vals);
}

export const normalizeCode = (code) => code.toUpperCase().replace(/[\s-]/g, '');

export function verifySpokenCode(code) {
  const c = normalizeCode(code);
  if (c.length !== 7) return { ok: false, code: c, reason: 'A spoken code has 7 characters. Please read it back.' };
  const bad = [...c].filter((ch) => !ALPHABET.includes(ch));
  if (bad.length) return { ok: false, code: c, reason: `"${bad.join('", "')}" is not in the alphabet (no 0, 1, I, L, O, or U). Please read it back.` };
  const vals = [...c].map((ch) => ALPHABET.indexOf(ch));
  const p = vals.slice(0, 6).reduce(step, M);
  const ok = (p + vals[6]) % M === 1;
  return { ok, code: c, reason: ok ? 'Check character matches.' : 'Check character does not match. Please read it back.' };
}
